import React, { useState, useEffect, useMemo, useRef } from 'react'
import st from './MergeDealsForm.module.scss'
import Button from 'components/CRM/UI/components/Button/Button'
import Loader from 'components/UI/Loader/Loader'
import swal from 'sweetalert'
import { superAxiosRequest } from 'axiosApi'

// Константы для стандартных полей
const STANDARD_FIELDS = [
  // 'phone',
  'manager',
  'budget',
  'city',
  'branch',
  'program',
  'level',
  'client',
  'source',
  'learning_days',
  'learning_goal',
  'learning_type',
  'reject_reason',
  'education_type',
]

const MergeDealsForm = ({
  dealsForMerge,
  onClose,
  onMerge,
  translate,
  funnelId,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [checkedState, setCheckedState] = useState({})
  const [selectedData, setSelectedData] = useState({})
  const [loading, setLoading] = useState(true)

  // Новые состояния для статусов
  const [statuses, setStatuses] = useState([])
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [selectedStatusName, setSelectedStatusName] = useState('')
  const [statusesLoading, setStatusesLoading] = useState(false)
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false)

  // Ref для отслеживания кликов вне селекта
  const statusDropdownRef = useRef(null)

  // Состояния для кастомных полей
  const [customFields, setCustomFields] = useState([])
  const [dealsCustomFieldsValues, setDealsCustomFieldsValues] = useState({})
  const [selectedCustomFields, setSelectedCustomFields] = useState({})

  // Закрытие дропдауна при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Загрузка статусов для воронки
  useEffect(() => {
    if (!funnelId) return

    const fetchStatuses = async () => {
      setStatusesLoading(true)
      try {
        const response = await superAxiosRequest({
          link: `integration/deal_statuses`,
          method: 'get',
          crm: true,
          params: {
            funnel_id: funnelId,
          },
        })
        const rawData = response.data
        const statusesArray = Object.keys(rawData).map((key) => ({
          ...rawData[key],
          id: key,
          position: parseInt(rawData[key].position),
        }))
        statusesArray.sort((a, b) => a.position - b.position)
        setStatuses(statusesArray)
      } catch (error) {
        console.error('Ошибка при загрузке статусов:', error)
      } finally {
        setStatusesLoading(false)
      }
    }

    fetchStatuses()
  }, [funnelId])

  // Загрузка кастомных полей для воронки
  useEffect(() => {
    if (!funnelId) return

    const fetchCustomFields = async () => {
      try {
        // Загружаем определение кастомных полей
        const fieldsResponse = await superAxiosRequest({
          link: `integration/additional_funnel_deal_fields`,
          method: 'get',
          crm: true,
          params: {
            funnel_id: funnelId,
          },
        })
        setCustomFields(fieldsResponse.data || [])

        // Загружаем значения кастомных полей для каждой сделки
        const dealsValues = {}
        for (const deal of dealsForMerge) {
          const valuesResponse = await superAxiosRequest({
            link: `integration/additional_funnel_deal_field_values`,
            method: 'get',
            crm: true,
            params: {
              funnel_id: funnelId,
              deal_id: deal.id,
            },
          })
          dealsValues[deal.id] = valuesResponse.data || []
        }
        setDealsCustomFieldsValues(dealsValues)
        setLoading(false)

        // Инициализируем состояние выбранных кастомных полей
        const initSelectedCustomFields = {}
        fieldsResponse.data?.forEach((field) => {
          initSelectedCustomFields[field.id] = {
            valueId: null,
            value: null,
          }
        })
        setSelectedCustomFields(initSelectedCustomFields)
      } catch (error) {
        setLoading(false)
        console.error('Ошибка при загрузке кастомных полей:', error)
      }
    }

    fetchCustomFields()
  }, [funnelId, dealsForMerge])

  // Инициализация состояний для стандартных полей
  useEffect(() => {
    const initCheckedState = STANDARD_FIELDS.reduce((acc, field) => {
      acc[field] = null
      return acc
    }, {})

    const initSelectedData = {
      // phone: '',
      manager: {
        id: null,
        full_name: '',
      },
      budget: '',
      branch: {
        id: null,
        name: '',
      },
      city: {
        id: null,
        name: '',
      },
      program: {
        id: null,
        name: '',
      },
      level: {
        id: null,
        name: '',
      },
      client: {
        id: null,
        name: '',
      },
      source: '',
      learning_days: '',
      learning_goal: '',
      learning_type: '',
      reject_reason: '',
      education_type: '',
      funnels: [
        {
          id: funnelId,
        },
      ],
    }

    setCheckedState(initCheckedState)
    setSelectedData(initSelectedData)
  }, [dealsForMerge, funnelId])

  // Функция для форматирования даты
  const formatClientDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Получение значения стандартного поля из сделки
  const getDealFieldValue = (deal, field) => {
    switch (field) {
      case 'client':
        return {
          id: deal?.client?.id || deal?.lead?.client?.id,
          name: deal?.client?.name || deal?.lead?.client?.name || '',
        }
      // case 'phone':
      //   return deal?.client?.phone || deal?.lead?.client?.phone || ''
      case 'source':
        return deal?.source?.name || deal?.source || ''
      default:
        if (deal[field] !== undefined && deal[field] !== null) {
          if (typeof deal[field] === 'object' && deal[field] !== null) {
            return { ...deal[field] }
          }
          return deal[field]
        }
        return field === 'budget' ? 0 : ''
    }
  }

  // Получение значения кастомного поля из сделки
  const getCustomFieldValue = (dealId, fieldId) => {
    const dealValues = dealsCustomFieldsValues[dealId] || []
    const fieldValue = dealValues.find(
      (value) => value.additional_funnel_field === fieldId,
    )
    return fieldValue
  }

  // Форматирование значения кастомного поля для отображения
  const formatCustomFieldValue = (field, value) => {
    if (!value) return 'Не указано'

    switch (field?.type) {
      case 'boolean':
        return value.value === true
          ? 'Да'
          : value.value === false
            ? 'Нет'
            : 'Не указано'
      case 'date':
        return value.value
          ? new Date(value.value).toLocaleDateString('ru-RU')
          : 'Не указано'
      case 'select':
        return value.value?.name || value.value || 'Не указано'
      default:
        return value.value || 'Не указано'
    }
  }

  // Обработчик выбора стандартного поля
  const handleFieldSelect = (field, dealId, value) => {
    setCheckedState((prev) => ({
      ...prev,
      [field]: dealId,
    }))

    setSelectedData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Обработчик выбора кастомного поля
  const handleCustomFieldSelect = (fieldId, dealId, value) => {
    setSelectedCustomFields((prev) => ({
      ...prev,
      [fieldId]: {
        valueId: value?.id || null,
        value: value?.value || null,
        fieldId: fieldId,
        dealId: dealId,
      },
    }))
  }

  // Обработчик выбора статуса
  const handleStatusSelect = (status) => {
    setSelectedStatus(status.id)
    setSelectedStatusName(status.name)
    setIsStatusDropdownOpen(false)
  }

  // Очистка выбранного статуса
  const handleClearStatus = () => {
    setSelectedStatus(null)
    setSelectedStatusName('')
    setIsStatusDropdownOpen(false)
  }

  // Переключение дропдауна
  const toggleStatusDropdown = () => {
    setIsStatusDropdownOpen(!isStatusDropdownOpen)
  }

  // Обработчик "Выбрать всё" для конкретной сделки
  const handleSelectAllFromDeal = (dealId, deal) => {
    // Выбираем все стандартные поля
    const newCheckedState = { ...checkedState }
    const newSelectedData = { ...selectedData }

    STANDARD_FIELDS.forEach((field) => {
      const value = getDealFieldValue(deal, field)
      newCheckedState[field] = dealId
      newSelectedData[field] = value
    })

    setCheckedState(newCheckedState)
    setSelectedData(newSelectedData)

    // Выбираем все кастомные поля из этой сделки
    const newSelectedCustomFields = { ...selectedCustomFields }

    // Проходим по всем кастомным полям, а не только по тем, у которых есть значения
    customFields.forEach((field) => {
      const fieldValue = getCustomFieldValue(dealId, field.id)
      newSelectedCustomFields[field.id] = {
        valueId: fieldValue?.id || null,
        value: fieldValue?.value || null,
        fieldId: field.id,
        dealId: dealId,
      }
    })

    setSelectedCustomFields(newSelectedCustomFields)
  }

  // Обработчик объединения
  const handleMerge = async () => {
    if (isLoading) return

    if (!selectedData.client?.name) {
      let errorMessage = 'Нужно заполнить обязательное поле - Имя клиента!'

      return swal('Ошибка!', errorMessage, 'warning')
    }

    setIsLoading(true)

    try {
      // Собираем ID выбранных значений кастомных полей
      const additionalFieldValuesIds = Object.values(selectedCustomFields)
        .filter((item) => item?.valueId) // Только выбранные поля
        .map((item) => item.valueId)

      // Формируем payload для API
      const payload = {
        combined_deals_ids: dealsForMerge.map((deal) => deal.id),
        deal: {
          // phone: selectedData.phone || null,
          manager: selectedData.manager?.id || null,
          budget: selectedData.budget || 0,
          branch: selectedData.branch?.id || null,
          city: selectedData.city?.id || null,
          program: selectedData.program?.id || null,
          level: selectedData.level?.id || null,
          funnels: [funnelId],
          client: selectedData.client?.id || null,
          source: selectedData.source || null,
          learning_days: selectedData.learning_days || null,
          learning_goal: selectedData.learning_goal || null,
          learning_type: selectedData.learning_type || null,
          reject_reason: selectedData.reject_reason || null,
          education_type: selectedData.education_type || null,
          status_deal_id: selectedStatus || null,
        },
        additional_funnel_deal_field_values_ids: additionalFieldValuesIds,
      }

      await onMerge(payload)
    } catch (error) {
      console.error('Ошибка при объединении сделок:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Рендер стандартного поля сделки
  const renderDealField = (deal, field, label, customValue = null) => {
    const value =
      customValue !== null ? customValue : getDealFieldValue(deal, field)
    const displayValue =
      typeof value === 'object' ? value.name || value.full_name || '' : value

    return (
      <div className={st.fieldRow} key={field}>
        <label className={st.fieldCheckboxLabel}>
          <input
            type='checkbox'
            checked={checkedState[field] === deal.id}
            onChange={() => handleFieldSelect(field, deal.id, value)}
            className={st.fieldCheckbox}
          />
          <span className={st.fieldName}>{label}</span>
        </label>
        <div className={st.fieldValue}>
          {field === 'budget' && value
            ? `${value} ₸`
            : field === 'deal_name'
              ? `#${displayValue}`
              : field === 'created_at' && value
                ? formatClientDate(value)
                : displayValue || 'Не указано'}
        </div>
      </div>
    )
  }

  // Рендер кастомного поля сделки
  const renderCustomField = (deal, field) => {
    const fieldValue = getCustomFieldValue(deal.id, field.id)
    const isSelected = selectedCustomFields[field.id]?.dealId === deal.id

    return (
      <div className={st.fieldRow} key={`custom-${field.id}-${deal.id}`}>
        <label className={st.fieldCheckboxLabel}>
          <input
            type='checkbox'
            checked={isSelected}
            onChange={() =>
              handleCustomFieldSelect(field.id, deal.id, fieldValue)
            }
            className={st.fieldCheckbox}
          />
          <span className={st.fieldName}>{field.name}</span>
        </label>
        <div className={st.fieldValue}>
          {formatCustomFieldValue(field, fieldValue)}
        </div>
      </div>
    )
  }

  // Рендер стандартного поля финальной сделки
  const renderFinalField = (field, label, isObject = false) => {
    const value = selectedData[field]
    const displayValue = isObject
      ? value?.name || value?.full_name || ''
      : value
    const isEmpty = !value || (isObject && !value.id)

    return (
      <div className={st.finalFieldRow} key={field}>
        <div className={st.finalFieldName}>{label}</div>
        <div
          className={`${st.finalFieldValue} ${isEmpty ? st.emptyField : ''}`}
        >
          {field === 'budget' && value
            ? `${value} ₸`
            : field === 'deal_name'
              ? `#${displayValue}`
              : field === 'created_at' && value
                ? formatClientDate(value)
                : displayValue || 'Не выбрано'}
        </div>
      </div>
    )
  }

  // Рендер кастомного поля финальной сделки
  const renderFinalCustomField = (field) => {
    const selectedValue = selectedCustomFields[field.id]
    const isEmpty = !selectedValue?.value

    return (
      <div className={st.finalFieldRow} key={`final-custom-${field.id}`}>
        <div className={st.finalFieldName}>{field.name}</div>
        <div
          className={`${st.finalFieldValue} ${isEmpty ? st.emptyField : ''}`}
        >
          {formatCustomFieldValue(field, { value: selectedValue?.value })}
        </div>
      </div>
    )
  }

  // Рендер кастомного селекта для выбора статуса
  const renderStatusSelect = () => (
    <div className={st.customSelectContainer} ref={statusDropdownRef}>
      <div
        className={`${st.customSelectTrigger} ${!selectedStatus ? st.empty : ''}`}
        onClick={toggleStatusDropdown}
      >
        <span className={st.selectedStatusText}>
          {selectedStatusName || 'Выбрать статус'}
        </span>
        <span className={`${st.arrow} ${isStatusDropdownOpen ? st.open : ''}`}>
          ▼
        </span>
      </div>

      {isStatusDropdownOpen && (
        <div className={st.customSelectDropdown}>
          {statusesLoading ? (
            <div className={st.dropdownLoading}>Загрузка...</div>
          ) : (
            <>
              <div className={st.dropdownOption} onClick={handleClearStatus}>
                <span className={st.optionName}>-- Не выбран --</span>
              </div>
              {statuses.map((status) => (
                <div
                  key={status.id}
                  className={`${st.dropdownOption} ${selectedStatus === status.id ? st.selected : ''}`}
                  onClick={() => handleStatusSelect(status)}
                >
                  <span
                    className={st.statusColor}
                    style={{ backgroundColor: status.color || '#ccc' }}
                  />
                  <span className={st.optionName}>{status.name}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )

  // Рендер карточки сделки
  const renderDealCard = (deal, index) => (
    <div key={deal.id} className={st.horizontalDealCard}>
      <div className={st.dealHeader}>
        <div className={st.dealTitle}>
          <div
            className={st.selectAllContainer}
            onClick={() => handleSelectAllFromDeal(deal.id, deal)}
          >
            Выбрать всё
          </div>
          <h3>Профиль клиента</h3>
        </div>
      </div>

      <div className={st.dealFieldsGrid}>
        {/* Основная информация о клиенте */}
        {renderDealField(deal, 'client', 'Имя клиента:')}
        {/*{renderDealField(deal, 'phone', 'Номер телефона:')}*/}

        {/* Разделитель */}
        <div className={st.fieldSeparator}></div>

        {/* Информация о сделке */}
        {renderDealField(deal, 'manager', 'Ответственный:')}
        {renderDealField(deal, 'budget', 'Бюджет:')}
        {renderDealField(deal, 'city', 'Город:')}
        {renderDealField(deal, 'branch', 'Филиал:')}
        {renderDealField(deal, 'program', 'Программа:')}
        {renderDealField(deal, 'level', 'Уровень:')}

        {/* Дополнительная информация */}
        <div className={st.fieldSeparator}></div>
        {renderDealField(deal, 'source', 'Источник:')}
        {renderDealField(deal, 'learning_days', 'Дни обучения:')}
        {renderDealField(deal, 'learning_goal', 'Цель обучения:')}
        {renderDealField(deal, 'learning_type', 'Тип обучения:')}
        {renderDealField(deal, 'reject_reason', 'Причина отказа:')}
        {renderDealField(deal, 'education_type', 'Тип образования:')}

        {/* Кастомные поля */}
        {customFields.length > 0 && (
          <>
            <div className={st.fieldSeparator}></div>
            {customFields.map((field) => renderCustomField(deal, field))}
          </>
        )}
      </div>
    </div>
  )

  // Рендер блока с окончательной сделкой
  const renderFinalDeal = () => (
    <div className={st.finalDealContainer}>
      <div className={st.wrapper}>
        <div className={st.finalDealHeader}>
          <h3>Объединенная сделка</h3>
          <div className={st.finalDealSubtitle}>
            Будет создана с выбранными полями
          </div>
        </div>

        <div className={st.finalDealCard}>
          <div className={st.finalFieldsGrid}>
            {/* Основная информация о клиенте */}
            {renderFinalField('client', 'Имя:', true)}
            {/*{renderFinalField('phone', 'Номер телефона:')}*/}

            {/* Разделитель */}
            <div className={st.finalFieldSeparator}></div>

            {/* Информация о сделке */}
            {renderFinalField('manager', 'Ответственный', true)}
            {renderFinalField('budget', 'Бюджет')}
            {renderFinalField('city', 'Город', true)}
            {renderFinalField('branch', 'Филиал', true)}
            {renderFinalField('program', 'Программа', true)}
            {renderFinalField('level', 'Уровень', true)}

            {/* Дополнительная информация */}
            <div className={st.finalFieldSeparator}></div>

            {renderFinalField('source', 'Источник')}
            {renderFinalField('learning_days', 'Дни обучения')}
            {renderFinalField('learning_goal', 'Цель обучения')}
            {renderFinalField('learning_type', 'Тип обучения')}
            {renderFinalField('reject_reason', 'Причина отказа')}
            {renderFinalField('education_type', 'Тип образования')}

            {/* Кастомные поля в финальной сделке */}
            {customFields.length > 0 && (
              <>
                <div className={st.finalFieldSeparator}></div>
                {customFields.map((field) => renderFinalCustomField(field))}
              </>
            )}

            <div className={st.finalFieldSeparator}></div>

            {/* Статус сделки - кастомный селект */}
            <div className={st.finalFieldRow}>
              <div className={st.finalFieldName}>Статус сделки:</div>
              <div className={st.finalFieldValue}>{renderStatusSelect()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className={st.mergeModalOverlay}>
      <div className={st.mergeModal}>
        <div className={st.mergeModalHeader}>
          <h2>Объединение сделок ({dealsForMerge.length})</h2>
          <button className={st.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={st.mergeModalContent}>
          {isLoading || loading || statusesLoading ? (
            <div className={st.loadingContainer}>
              <Loader />
              {isLoading && <p>Объединение сделок...</p>}
            </div>
          ) : (
            <>
              <div className={st.mergeInstructions}>
                <p>
                  Выберите поля для объединенной сделки. Отметьте чекбоксы рядом
                  с нужными значениями. Можно выбрать "Выбрать всё" для
                  копирования всех полей из одной сделки.
                </p>
              </div>

              <div className={st.horizontalMergeContainer}>
                <div className={st.dealsRowContainer}>
                  <div className={st.dealsRow}>
                    {dealsForMerge.map((deal, index) =>
                      renderDealCard(deal, index),
                    )}
                  </div>
                </div>

                {renderFinalDeal()}
              </div>

              <div className={st.mergeActions}>
                <Button
                  text='Отмена'
                  onClick={onClose}
                  className={st.cancelButton}
                  isDisabled={isLoading}
                />
                <Button
                  text={isLoading ? 'Объединение...' : 'Объединить сделки'}
                  onClick={handleMerge}
                  className={st.mergeButton}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default MergeDealsForm
